import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import Spinner from '../spinner/spinner';
import { createPagination, formatDate2, formatPrice } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { REQUEST_STATUS, USER_ROLES } from '../../utils/constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StatusPill from '../statuspill/statuspill.component';
import useFetchRequests from './hooks/useFetchRequests';
import './requeststable.component.css';

const TableHeaders = () => {
    const userInfo = useSelector(state => state.auth.user);

    const patientHeaders = (
        <>
            <th scope="col">Request For</th>
            <th scope="col">Service Type</th>
            <th scope="col">Patient Name</th>
            <th scope="col">Patient Number</th>
            <th scope="col">Patient Age</th>
            <th scope="col" data-type="currency">Amount</th>
            <th scope="col">Date</th>
            <th scope="col">Status</th>
        </>
    );

    const execHeaders = (
        <>
            <th scope="col">Request For</th>
            <th scope="col">Patient Name</th>
            <th scope="col">Patient Number</th>
            <th scope="col">Patient Age</th>
            <th scope="col">Date</th>
            <th scope="col">Status</th>
        </>
    );

    const userRole = userInfo && userInfo.role;
    const renderHeaders = () => {
        switch (userRole) {
            case USER_ROLES.FIELD_EXECUTIVE:
                return execHeaders;
            default:
                return patientHeaders;
        }
    };

    return (
        <>{renderHeaders()}</>
    );
};

const RequestItem = ({request}) => {
    const userInfo = useSelector(state => state.auth.user);
    const navigate = useNavigate();
    const userRole = userInfo && userInfo.role;

    const showRequestDetals = () => {
        switch (userRole) {
            case USER_ROLES.SUPER_ADMIN:
            case USER_ROLES.FIELD_EXECUTIVE:
            case USER_ROLES.VENDOR:
                navigate(`/admin/requests/${request.uniqueId}`);
                return;
            default:
                navigate(`/my-requests/${request.uniqueId}`);
                return;
        }
    };

    const patientInfo = JSON.parse(request.patientInfo);
    const patientFields = (
        <>
            <th scope="row">{request.requestFor}</th>
            <td data-title="Service Type">{request.testServiceType}</td>
            <td data-title="Patient Name">{patientInfo.patientName}</td>
            <td data-title="Patient Number">{patientInfo.patientNumber}</td>
            <td data-title="Patient Age">{patientInfo.patientAge}</td>
            <td data-title="Amount" data-type="currency">{formatPrice(request.amount)}</td>
            <td data-title="Date">{formatDate2(request.createdAt)}</td>
            <td data-title="Status"><StatusPill status={request.status} /></td>
        </>
    );

    const execFields = (
        <>
            <th scope="row">{request.requestFor}</th>
            <td data-title="Patient Name">{patientInfo.patientName}</td>
            <td data-title="Patient Number">{patientInfo.patientNumber}</td>
            <td data-title="Patient Age">{patientInfo.patientAge}</td>
            <td data-title="Date">{formatDate2(request.createdAt)}</td>
            <td data-title="Status"><StatusPill status={request.status} /></td>
        </>
    );

    const renderFields = () => {
        switch (userRole) {
            case USER_ROLES.FIELD_EXECUTIVE:
                return execFields;
            default:
                return patientFields;
        }
    };

    return (
        <tr onClick={showRequestDetals}>
            {renderFields()}
        </tr>
    );
};
RequestItem.propTypes = {
    request: PropTypes.object.isRequired
};

const RequestsTable = () => {
    const userInfo = useSelector(state => state.auth.user);
    const { requests, loading, pageData, fetchData } = useFetchRequests();
    const [searchParams, setSearchParams] = useSearchParams();
    const filterQuery = searchParams.get('status') || '';
    const isAdmin = userInfo && userInfo.role && userInfo.role === USER_ROLES.SUPER_ADMIN;
    const isFexec = userInfo && userInfo.role && userInfo.role === USER_ROLES.FIELD_EXECUTIVE;

    const handlePagination = (page) => () => {
        if (pageData.page === page) return;
        fetchData(page);
    };

    const requestList = () => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={7} className='empty-requests'>
                        <div className='centerContent'><Spinner /> </div>
                        <Typography>Loading</Typography>
                    </td>
                </tr>
            );
        }

        const filteredReqs = !filterQuery ? requests : requests.filter(request => request.status == filterQuery.toUpperCase());

        if (filteredReqs.length < 1) {
            return (
                <tr>
                    <td colSpan="7" className='empty-requests'>
                        <Typography variant='subtitle1'>No Requests</Typography>
                    </td>
                </tr>
            );
        }

        return filteredReqs.map((request, i) => <RequestItem key={i} request={request} />);
        // return requests.map((request, i) => <RequestItem key={i} request={request} />);
    };

    const statusFiltersUI = () => {
        const handleChange = (event) => {
            let paramData = {};
            if (event.target.value) {
                paramData = { status: event.target.value.toUpperCase() };
            }
            setSearchParams(paramData);
        };

        const renderValue = (selected) => {
            if (selected === '') {
                const emptyLabel = <span>All</span>;
                return emptyLabel;
            }
            const nonEmptyLabel = <span style={{textTransform: 'capitalize'}}>{selected.toLowerCase().replace(/_/g, ' ')}</span>;
            return nonEmptyLabel;
        };
        return (
            <Box sx={{ width: { sm: '100%', md: '20%' }, marginLeft: { sm: 0, md: 'auto !important' } }}>
                <FormControl fullWidth size='small'>
                    <InputLabel id="request-status-select-label" shrink={true}> Filter by Status </InputLabel>
                    <Select
                        labelId="request-status-select-label"
                        value={filterQuery}
                        label="Filter by Status"
                        onChange={handleChange}
                        displayEmpty
                        defaultValue=''
                        renderValue={renderValue}
                    >
                        <MenuItem value=''>All</MenuItem>
                        <MenuItem value={REQUEST_STATUS.PENDING}>Pending</MenuItem>
                        <MenuItem value={REQUEST_STATUS.CONFIRMED}>Confirmed</MenuItem>
                        <MenuItem value={REQUEST_STATUS.CANCELLED}>Cancelled</MenuItem>
                        <MenuItem value={REQUEST_STATUS.COMPLETED}>Completed</MenuItem>
                        <MenuItem value={REQUEST_STATUS.ACCEPTED}>Accepted</MenuItem>
                        <MenuItem value={REQUEST_STATUS.SAMPLE_COLLECTED}>Sample Collected</MenuItem>
                        <MenuItem value={REQUEST_STATUS.SAMPLE_SUBMITTED}>Sample Submitted</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        );
    };

    // const paginationUI = createPagination(pageData.totalPages, pageData.page).map((page, i) => <div key={i}>{page}</div>);
    const paginationUI = createPagination(pageData.totalPages, pageData.page).map((page, i) => {
        if (page === '...') {
            return <div key={i}>{page}</div>;
        }
        const styles = {cursor: 'not-allowed', color: 'black'};
        let variant = 'outlined';
        if (pageData.page !== page) {
            variant = '';
            styles.color = 'white';
            styles.cursor = 'pointer';
        }
        return <Chip key={i} label={page} style={styles} onClick={handlePagination(page)} color='primary' variant={variant} />;
    });

    const paginationSection = requests && requests.length > 1 && pageData.totalPages > 1 && (
        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem'}}>
            {/* <Chip label={page} style={styles} onClick={handlePagination(page)} color='primary' variant={variant} /> */}
            {paginationUI}
        </div>
    );

    const tableTitle = () => {
        if (isAdmin) {
            return 'User Requests';
        }
        if (isFexec) {
            return 'Requests';
        }

        return 'My Requests';
    };

    return (
        <div>
            <div className='requests-table-component'>
                <div className="container">
                    <table className="responsive-table">
                    <caption>
                        <Typography variant='h5'>
                            <Stack
                                direction={{ sm: 'column', md: 'row' }}
                                // spacing={{ xs: 12, sm: 12, md: 4 }}
                            >
                                {tableTitle()}
                                {statusFiltersUI()}
                            </Stack>
                        </Typography>
                    </caption>
                    <thead>
                        <tr>
                            <TableHeaders />
                        </tr>
                    </thead>
                    <tfoot>
                        <tr>
                            <td colSpan="8"></td>
                        </tr>
                        <tr>
                            <td colSpan="8"></td>
                        </tr>
                        <tr>
                            <td colSpan="8">
                                {paginationSection}
                            </td>
                        </tr>
                    </tfoot>
                    <tbody>{requestList()}</tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RequestsTable;